import { Currency } from '@kodiak-finance/sdk-core'
import CurrencyLogo from 'components/CurrencyLogo'
import { MouseoverTooltip } from 'components/Tooltip'
import { useMemo } from 'react'
import { formatPrice } from 'utils/formatCurrencyAmount'
import { getOptimalSigFigs } from 'utils/formatTickPrice'

import { RangeLineItem, RangeText } from './styled'

interface CurrentPriceDisplayProps {
  currencyBase?: Currency | null
  currencyQuote?: Currency | null
  quotePrice?: any
  formattedQuotePrice?: string | null
}

export const CurrentPriceDisplay = ({
  currencyBase,
  currencyQuote,
  quotePrice,
  formattedQuotePrice,
}: CurrentPriceDisplayProps) => {
  const inversePriceFormatted = useMemo(() => {
    if (!quotePrice) return null

    try {
      const inversePrice = quotePrice.invert()
      return inversePrice.lessThan(1)
        ? inversePrice.toSignificant(5)
        : formatPrice(inversePrice, getOptimalSigFigs(inversePrice))
    } catch (error) {
      console.error('Error calculating inverse price:', error)
      return null
    }
  }, [quotePrice])

  const displayFormattedPrice = useMemo(() => {
    if (!quotePrice) return formattedQuotePrice

    return (
      formattedQuotePrice ||
      (quotePrice.lessThan(1) ? quotePrice.toSignificant(5) : formatPrice(quotePrice, getOptimalSigFigs(quotePrice)))
    )
  }, [quotePrice, formattedQuotePrice])

  return useMemo(() => {
    const priceContent = (
      <RangeText>
        <CurrencyLogo size="14px" currency={currencyBase} />
        <span>1 =</span>
        <CurrencyLogo size="14px" currency={currencyQuote} />
        <span>{displayFormattedPrice}</span>
      </RangeText>
    )

    if (!quotePrice) {
      return (
        <RangeLineItem>
          <RangeText>_</RangeText>
        </RangeLineItem>
      )
    }

    return (
      <MouseoverTooltip
        text={
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <RangeText>
              <CurrencyLogo size="14px" currency={currencyBase} />
              <span>1 {currencyBase?.symbol} =</span>
              <CurrencyLogo size="14px" currency={currencyQuote} />
              <span>
                {displayFormattedPrice} {currencyQuote?.symbol}
              </span>
            </RangeText>
            {inversePriceFormatted && (
              <RangeText>
                <CurrencyLogo size="14px" currency={currencyQuote} />
                <span>1 {currencyQuote?.symbol} =</span>
                <CurrencyLogo size="14px" currency={currencyBase} />
                <span>
                  {inversePriceFormatted} {currencyBase?.symbol}
                </span>
              </RangeText>
            )}
          </div>
        }
        delay={500}
      >
        {priceContent}
      </MouseoverTooltip>
    )
  }, [currencyBase, currencyQuote, quotePrice, displayFormattedPrice, inversePriceFormatted])
}

export default CurrentPriceDisplay
